import { useState, useCallback, useMemo } from 'react';
import { trpc } from '../../lib/trpc';
import { MediaGrid } from './MediaGrid';
import { MediaUpload } from './MediaUpload';
import { MediaDetail } from './MediaDetail';
import { MediaFilters } from './MediaFilters';
import { MediaControls } from './MediaControls';
import type { Media, EntityType } from '@wineryxplorer/shared';

export interface MediaLibraryProps {
  entityType: EntityType;
  entityId: string;
  canUpload?: boolean;
  canSetHero?: boolean;
  canManageGallery?: boolean;
  showSharedMedia?: boolean;
  className?: string;
}

export function MediaLibrary({
  entityType,
  entityId,
  canUpload = true,
  canSetHero = true,
  canManageGallery = true,
  showSharedMedia = false,
  className = '',
}: MediaLibraryProps) {
  // State
  const [selectedMedia, setSelectedMedia] = useState<Set<string>>(new Set());
  const [detailMedia, setDetailMedia] = useState<Media | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [publicationFilter, setPublicationFilter] = useState<'all' | 'published' | 'unpublished'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [includeShared, setIncludeShared] = useState(showSharedMedia);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // tRPC queries and mutations
  const utils = trpc.useContext();

  const { data: mediaLibrary, isPending: isLoadingMedia, refetch: refetchMedia } = trpc.media.getMediaLibrary.useQuery({
    entityType,
    entityId,
    tags: selectedTags.length > 0 ? selectedTags : undefined,
    isPublished: publicationFilter === 'all' ? undefined : publicationFilter === 'published',
    includeShared,
    limit: 100,
  });

  const { data: availableTags } = trpc.media.getTagsForEntity.useQuery({
    entityType,
    entityId,
  });

  // Mutations
  const togglePublicationMutation = trpc.media.togglePublication.useMutation({
    onSuccess: () => {
      refetchMedia();
      utils.media.getMediaLibrary.invalidate();
    },
  });

  const bulkTogglePublicationMutation = trpc.media.bulkTogglePublication.useMutation({
    onSuccess: () => {
      refetchMedia();
      utils.media.getMediaLibrary.invalidate();
      setSelectedMedia(new Set());
    },
  });

  const setAsHeroMutation = trpc.media.setAsHeroImage.useMutation({
    onSuccess: () => {
      refetchMedia();
      utils.venue.getById.invalidate({ id: entityId });
    },
  });

  const addToGalleryMutation = trpc.media.addToGallery.useMutation({
    onSuccess: () => {
      refetchMedia();
      utils.venue.getById.invalidate({ id: entityId });
    },
  });

  const removeFromGalleryMutation = trpc.media.removeFromGallery.useMutation({
    onSuccess: () => {
      refetchMedia();
      utils.venue.getById.invalidate({ id: entityId });
    },
  });

  const deleteMutation = trpc.media.delete.useMutation({
    onSuccess: () => {
      refetchMedia();
      utils.media.getMediaLibrary.invalidate();
      setSelectedMedia(new Set());
    },
  });

  const bulkDeleteMutation = trpc.media.bulkDelete.useMutation({
    onSuccess: () => {
      refetchMedia();
      utils.media.getMediaLibrary.invalidate();
      setSelectedMedia(new Set());
    },
  });

  const updateTagsMutation = trpc.media.updateTags.useMutation({
    onSuccess: () => {
      refetchMedia();
      utils.media.getTagsForEntity.invalidate({ entityType, entityId });
    },
  });

  const useSharedMediaMutation = trpc.media.useSharedMedia.useMutation({
    onSuccess: () => {
      refetchMedia();
      utils.venue.getById.invalidate({ id: entityId });
    },
  });

  // Filter media based on search query (client-side for now)
  const filteredMedia = useMemo(() => {
    // Cast media to proper type since tRPC returns unknown[]
    const mediaList = (mediaLibrary?.media ?? []) as Media[];
    return mediaList.filter((media) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        media.filename.toLowerCase().includes(query) ||
        media.caption?.toLowerCase().includes(query) ||
        media.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    });
  }, [mediaLibrary?.media, searchQuery]);

  // Handlers
  const handleSelectMedia = useCallback((media: Media) => {
    setDetailMedia(media);
    setIsDetailOpen(true);
  }, []);

  const handleToggleSelectMedia = useCallback((media: Media) => {
    setSelectedMedia(prev => {
      const newSet = new Set(prev);
      if (newSet.has(media.id)) {
        newSet.delete(media.id);
      } else {
        newSet.add(media.id);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedMedia.size === filteredMedia.length) {
      setSelectedMedia(new Set());
    } else {
      setSelectedMedia(new Set(filteredMedia.map((m) => m.id)));
    }
  }, [selectedMedia.size, filteredMedia]);

  const handleUploadSuccess = useCallback(() => {
    refetchMedia();
  }, [refetchMedia]);

  const handlePublish = useCallback((mediaIds: string[]) => {
    if (mediaIds.length === 1) {
      togglePublicationMutation.mutate({ id: mediaIds[0], isPublished: true });
    } else {
      bulkTogglePublicationMutation.mutate({ mediaIds, isPublished: true });
    }
  }, [togglePublicationMutation, bulkTogglePublicationMutation]);

  const handleUnpublish = useCallback((mediaIds: string[]) => {
    if (mediaIds.length === 1) {
      togglePublicationMutation.mutate({ id: mediaIds[0], isPublished: false });
    } else {
      bulkTogglePublicationMutation.mutate({ mediaIds, isPublished: false });
    }
  }, [togglePublicationMutation, bulkTogglePublicationMutation]);

  const handleSetAsHero = useCallback((mediaId: string) => {
    if (entityType === 'venue') {
      setAsHeroMutation.mutate({ mediaId, venueId: entityId });
    }
  }, [entityType, entityId, setAsHeroMutation]);

  const handleAddToGallery = useCallback((mediaIds: string[]) => {
    if (entityType === 'venue') {
      mediaIds.forEach(mediaId => {
        addToGalleryMutation.mutate({ mediaId, venueId: entityId });
      });
    }
  }, [entityType, entityId, addToGalleryMutation]);

  const handleRemoveFromGallery = useCallback((mediaIds: string[]) => {
    if (entityType === 'venue') {
      mediaIds.forEach(mediaId => {
        removeFromGalleryMutation.mutate({ mediaId, venueId: entityId });
      });
    }
  }, [entityType, entityId, removeFromGalleryMutation]);

  const handleDelete = useCallback((mediaIds: string[]) => {
    if (mediaIds.length === 1) {
      deleteMutation.mutate({ id: mediaIds[0] });
    } else {
      bulkDeleteMutation.mutate({ mediaIds });
    }
  }, [deleteMutation, bulkDeleteMutation]);

  const handleUpdateTags = useCallback((mediaId: string, tags: string[]) => {
    updateTagsMutation.mutate({ id: mediaId, tags });
  }, [updateTagsMutation]);

  const handleUseSharedMedia = useCallback((sharedMediaId: string, action: 'setAsHero' | 'addToGallery') => {
    if (entityType === 'venue') {
      useSharedMediaMutation.mutate({ 
        sharedMediaId, 
        venueId: entityId, 
        action 
      });
    }
  }, [entityType, entityId, useSharedMediaMutation]);

  const getSelectedMediaObjects = useCallback(() => {
    return filteredMedia.filter((media) => selectedMedia.has(media.id));
  }, [filteredMedia, selectedMedia]);

  const isMutating = 
    togglePublicationMutation.isPending ||
    bulkTogglePublicationMutation.isPending ||
    setAsHeroMutation.isPending ||
    addToGalleryMutation.isPending ||
    removeFromGalleryMutation.isPending ||
    deleteMutation.isPending ||
    bulkDeleteMutation.isPending ||
    updateTagsMutation.isPending ||
    useSharedMediaMutation.isPending;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Media Library</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage photos for this {entityType}
            {mediaLibrary && ` • ${mediaLibrary.total} items`}
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          {selectedMedia.size > 0 && (
            <button
              onClick={() => setShowBulkActions(!showBulkActions)}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Bulk Actions ({selectedMedia.size})
            </button>
          )}
          
          {filteredMedia.length > 0 && (
            <button
              onClick={handleSelectAll}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {selectedMedia.size === filteredMedia.length ? 'Deselect All' : 'Select All'}
            </button>
          )}
        </div>
      </div>

      {/* Upload Section */}
      {canUpload && (
        <MediaUpload
          entityType={entityType}
          entityId={entityId}
          onUploadSuccess={handleUploadSuccess}
          className="bg-gray-50 border border-gray-200 rounded-lg p-6"
        />
      )}

      {/* Filters */}
      <MediaFilters
        availableTags={availableTags?.tags}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
        publicationFilter={publicationFilter}
        onPublicationFilterChange={setPublicationFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showSharedMedia={includeShared}
        onShowSharedMediaChange={setIncludeShared}
      />

      {/* Bulk Actions */}
      {showBulkActions && selectedMedia.size > 0 && (
        <MediaControls
          selectedMedia={getSelectedMediaObjects()}
          onPublish={handlePublish}
          onUnpublish={handleUnpublish}
          onSetAsHero={canSetHero ? handleSetAsHero : undefined}
          onAddToGallery={canManageGallery ? handleAddToGallery : undefined}
          onRemoveFromGallery={canManageGallery ? handleRemoveFromGallery : undefined}
          onDelete={handleDelete}
          isLoading={isMutating}
          canSetHero={canSetHero}
          canManageGallery={canManageGallery}
        />
      )}

      {/* Media Grid */}
      <MediaGrid
        media={filteredMedia}
        selectedMedia={selectedMedia}
        onSelectMedia={handleSelectMedia}
        onToggleSelectMedia={handleToggleSelectMedia}
        showCheckboxes={true}
        showPublicationStatus={true}
        showTags={true}
        isLoading={isLoadingMedia}
        emptyMessage={
          searchQuery || selectedTags.length > 0 || publicationFilter !== 'all'
            ? 'No media matches your filters'
            : 'No media uploaded yet'
        }
      />

      {/* Media Detail Modal */}
      <MediaDetail
        media={detailMedia}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setDetailMedia(null);
        }}
        onPublish={(mediaId) => handlePublish([mediaId])}
        onUnpublish={(mediaId) => handleUnpublish([mediaId])}
        onSetAsHero={canSetHero ? handleSetAsHero : undefined}
        onAddToGallery={canManageGallery ? (mediaId) => handleAddToGallery([mediaId]) : undefined}
        onRemoveFromGallery={canManageGallery ? (mediaId) => handleRemoveFromGallery([mediaId]) : undefined}
        onDelete={(mediaId) => handleDelete([mediaId])}
        onUpdateTags={handleUpdateTags}
        onUseSharedMedia={handleUseSharedMedia}
        isLoading={isMutating}
        canSetHero={canSetHero}
        canManageGallery={canManageGallery}
        venueId={entityType === 'venue' ? entityId : undefined}
      />
    </div>
  );
}